import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { MdCheckCircle } from 'react-icons/md';

import {
  Body,
  ButtonContainer,
  CnpjPTag,
  CompanyDescription,
  CompanyNameTitle,
  Container,
  Header,
  Logo,
  ProductDetailsContainer,
  SuccessMessage,
  HeaderMeta,
} from './styles';

import companyRepository from '../../lib/company';
import { Button, FieldContainer, Input, Text } from '../../components';
import Loading from '../../components/Loading';
import { CompanyMarketDTO } from '../../dtos/CompanyDTO';

interface Params {
  value?: string;
}

export default function Company() {
  const { value } = useParams<Params>();

  const [forum, setForum] = useState({
    advertiserName: '',
    advertiserWebsite: '',
  });
  
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [didRespond, setDidRespond] = useState(false);
  const [company, setCompany] = useState<CompanyMarketDTO>();

  const findCompany = useCallback(async (code: string) => {
    setLoadingCompany(true);
    try {
      const data = await companyRepository.find(code);
      setCompany(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(msg);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  useEffect(() => {
    if (value) {
      findCompany(value);
    }
  }, [value, findCompany]);

  const submit = async () => {
    if (!value) {
      toast.error('Nenhum valor informado.');
      return;
    }
    
    if (!forum.advertiserName || !forum.advertiserWebsite) {
      toast.error('Por favor, preencha todos os campos antes de enviar.');
      return;
    }

    setIsSubmitting(true);
    try {
      await companyRepository.answerQuestionsCompany({
        ...forum,
        licensedQrCodeId: value,
      });
      toast.success('Mensagem enviada com sucesso!');
      setDidRespond(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Logo />
        <HeaderMeta>
          Exibido em: {format(new Date(), 'dd/MM/yyyy HH:mm')}
        </HeaderMeta>
      </Header>

      {loadingCompany ? (
        <Loading text="Carregando dados da empresa..." />
      ) : (
        <Body>
          <ProductDetailsContainer>
            <CompanyNameTitle>{company?.company?.name || 'Empresa não encontrada'}</CompanyNameTitle>
            <CnpjPTag>CNPJ: {company?.company?.document}</CnpjPTag>
            {company?.observation && (
              <CompanyDescription>{company.observation}</CompanyDescription>
            )}
          </ProductDetailsContainer>

          <ProductDetailsContainer>
            {didRespond ? (
              <SuccessMessage>
                <MdCheckCircle />
                <h4>Informação enviada</h4>
                <p>Obrigado por ajudar a manter a rede segura.</p>
              </SuccessMessage>
            ) : (
              <>
                <CompanyDescription>
                  Caso as informações ou o local (site) de venda do anunciante
                  não sejam esses, informe aqui o nome do anunciante e o local
                  (site) onde você leu esse QRCode.
                </CompanyDescription>

                <FieldContainer>
                  <Text bold>Nome do anunciante</Text>
                  <Input
                    type="text"
                    name="advertiserName"
                    value={forum.advertiserName}
                    placeholder="Ex: Loja XYZ"
                    onChange={(e) => {
                      setForum((prev) => ({
                        ...prev,
                        advertiserName: e.target.value,
                      }));
                    }}
                  />
                </FieldContainer>

                <FieldContainer>
                  <Text bold>Local (site) onde está o anúncio</Text>
                  <Input
                    type="text"
                    name="advertiserWebsite"
                    value={forum.advertiserWebsite}
                    placeholder="Ex: www.lojaxyz.com.br"
                    onChange={(e) => {
                      setForum((prev) => ({
                        ...prev,
                        advertiserWebsite: e.target.value,
                      }));
                    }}
                  />
                </FieldContainer>

                <ButtonContainer>
                  <div>
                    <Button type="submit" onClick={submit} disabled={isSubmitting}>
                      {isSubmitting ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </ButtonContainer>
              </>
            )}
          </ProductDetailsContainer>
        </Body>
      )}
    </Container>
  );
}
